import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { PipelineSection } from '@/components/admin/processing/PipelineSection'
import { ProcessingRangeToggle } from '@/components/admin/processing/ProcessingRangeToggle'
import { PageShell } from '@/components/layout/PageShell'
import { Card, CardContent } from '@/components/ui/card'
import { useProcessingState } from '@/features/admin/hooks/useProcessingState'
import { RANGE_OPTIONS, REFRESH_FAST_MS, REFRESH_SLOW_MS } from '@/features/admin/processing-format'

export default function AdminProcessingPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const rangeKey = searchParams.get('window') ?? '24h'
  const rangeHours = RANGE_OPTIONS.find(r => r.key === rangeKey)?.hours ?? 24
  const rawRangeLabel = RANGE_OPTIONS.find(r => r.key === rangeKey)?.label ?? '24h'
  // The numeric range labels (1h, 24h…) are notation; only "All" is a word.
  const rangeLabel = rangeKey === 'all' ? t('admin.processing.range.all') : rawRangeLabel
  const refreshMs = rangeHours >= 24 * 7 ? REFRESH_SLOW_MS : REFRESH_FAST_MS

  const { state, loading, error } = useProcessingState(rangeHours, refreshMs)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const setRangeKey = useCallback(
    (key: string) => {
      const next = new URLSearchParams(searchParams)
      if (key === '24h') next.delete('window')
      else next.set('window', key)
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const toggle = (key: string) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }))

  const pipelines = state?.pipelines ?? []

  return (
    <PageShell>
      <div className="space-y-4 pt-4 md:p-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-0">
          <div>
            <h1 className="text-2xl font-semibold">{t('admin.processing.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('admin.processing.subtitle')}</p>
          </div>
          <ProcessingRangeToggle rangeKey={rangeKey} refreshMs={refreshMs} onSelect={setRangeKey} />
        </div>

        {error && (
          <Card className="border-destructive">
            <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
          </Card>
        )}

        {loading && !state ? (
          <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : (
          pipelines.map(p => (
            <PipelineSection
              key={p.key}
              pipeline={p}
              rangeLabel={rangeLabel}
              collapsed={!!collapsed[p.key]}
              onToggle={() => toggle(p.key)}
            />
          ))
        )}
      </div>
    </PageShell>
  )
}
